// File: src/pages/TermsOfServicePage.jsx

import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Page,
  Layout,
  TextContainer,
  Text,
  List as PolarisList,
  Link,
} from '@shopify/polaris';


export default function TermsOfServicePage() {
  const navigate = useNavigate();
  const location = useLocation();

  const handleBack = () => {
    // Check if it's likely the first route loaded in the session (key 'default')
    // or if the history is very short. If so, default to homepage ('/').
    if (location.key === 'default' || window.history.length <= 2) {
      navigate('/', { replace: true });
    } else {
      // Otherwise, safely go back one step in history
      navigate(-1);
    }
  };

  return (
    <Page
      title="BaoBrain Terms of Service"
      backAction={{
        content: 'Back', 
        onAction: handleBack, 
      }}
    >
      <Layout>
        <Layout.Section>
  
        </Layout.Section>
        <Layout.Section>
          <TextContainer>
            <Text variant="headingMd" as="h2" marginBlockStart="4">
              Table of Contents
            </Text>
            <PolarisList type="bullet" marginBlockStart="2">
              <PolarisList.Item><Link url="#acceptance">1. Acceptance of Terms</Link></PolarisList.Item>
              <PolarisList.Item><Link url="#definitions">2. Definitions</Link></PolarisList.Item>
              <PolarisList.Item><Link url="#service-description">3. Service Description</Link></PolarisList.Item>
              <PolarisList.Item><Link url="#eligibility">4. Eligibility & Account Registration</Link></PolarisList.Item>
              <PolarisList.Item><Link url="#data-processing">5. Data Processing & Ownership</Link></PolarisList.Item>
              <PolarisList.Item><Link url="#merchant-responsibilities">6. Merchant Responsibilities</Link></PolarisList.Item>
              <PolarisList.Item><Link url="#prohibited-use">7. Prohibited Uses</Link></PolarisList.Item>
              <PolarisList.Item><Link url="#license">8. License Grant & Restrictions</Link></PolarisList.Item>
              <PolarisList.Item><Link url="#payment">9. Payment & Billing</Link></PolarisList.Item>
              <PolarisList.Item><Link url="#privacy">10. Privacy & Data Protection</Link></PolarisList.Item>
              <PolarisList.Item><Link url="#ip">11. Intellectual Property</Link></PolarisList.Item>
              <PolarisList.Item><Link url="#disclaimers">12. Disclaimers & Warranties</Link></PolarisList.Item>
              <PolarisList.Item><Link url="#liability">13. Limitation of Liability</Link></PolarisList.Item>
              <PolarisList.Item><Link url="#indemnity">14. Indemnification</Link></PolarisList.Item>
              <PolarisList.Item><Link url="#termination">15. Termination</Link></PolarisList.Item>
              <PolarisList.Item><Link url="#dispute">16. Dispute Resolution & Arbitration</Link></PolarisList.Item>
              <PolarisList.Item><Link url="#service-availability">17. Service Availability & Modifications</Link></PolarisList.Item>
              <PolarisList.Item><Link url="#beta">18. Beta Features</Link></PolarisList.Item>
              <PolarisList.Item><Link url="#force-majeure">19. Force Majeure</Link></PolarisList.Item>
              <PolarisList.Item><Link url="#general">20. General Provisions</Link></PolarisList.Item>
              <PolarisList.Item><Link url="#contact">21. Contact Information</Link></PolarisList.Item>
            </PolarisList>

            <Text variant="bodyMd" as="p" fontWeight="semibold" marginBlockStart="6">
              Last Updated: December 3, 2025
            </Text>

            {/* 1. ACCEPTANCE */}
            <Text variant="headingMd" as="h2" marginBlockStart="8" id="acceptance">
              1. Acceptance of Terms
            </Text>
            <Text variant="bodyMd" as="p" marginBlockStart="2">
              Welcome to BaoBrain. These Terms of Service ("Terms") constitute a legally binding agreement between you and BaoBrain ("we," "us," "our") governing your access to and use of https://baobrain.com (the "Site") and all related services, apps, APIs, and features (collectively, the "Services").
            </Text>
            <Text variant="bodyMd" as="p" marginBlockStart="2">
              <Text as="span" fontWeight="semibold">By accessing or using the Services, you agree to be bound by these Terms.</Text> If you do not agree with any part of these Terms, you must not use the Services.
            </Text>
            <Text variant="bodyMd" as="p" marginBlockStart="2">
              We reserve the right to modify these Terms at any time. Continued use of the Services after changes are posted constitutes acceptance of the modified Terms.
            </Text>

            {/* 2. DEFINITIONS */}
            <Text variant="headingMd" as="h2" marginBlockStart="8" id="definitions">
              2. Definitions
            </Text>
            <PolarisList type="bullet" marginBlockStart="2">
              <PolarisList.Item>
                <Text as="span" fontWeight="semibold">"Account"</Text> means your registered merchant account on BaoBrain.
              </PolarisList.Item>
              <PolarisList.Item>
                <Text as="span" fontWeight="semibold">"Merchant" or "You"</Text> means any person or entity who registers for and uses BaoBrain's Services.
              </PolarisList.Item>
              <PolarisList.Item>
                <Text as="span" fontWeight="semibold">"Store"</Text> means your e-commerce website or platform (Shopify, WooCommerce, etc.) where BaoBrain is installed.
              </PolarisList.Item>
              <PolarisList.Item>
                <Text as="span" fontWeight="semibold">"Analytics Data"</Text> means anonymous behavioral data collected from your Store's visitors.
              </PolarisList.Item>
              <PolarisList.Item>
                <Text as="span" fontWeight="semibold">"Customer"</Text> means an end user who visits or shops on your Store.
              </PolarisList.Item>
              <PolarisList.Item>
                <Text as="span" fontWeight="semibold">"Personal Data" or "PII"</Text> means personally identifiable information such as names, emails, phone numbers, or addresses.
              </PolarisList.Item>
            </PolarisList>

            {/* 3. SERVICE DESCRIPTION */}
            <Text variant="headingMd" as="h2" marginBlockStart="8" id="service-description">
              3. Service Description
            </Text>
            <Text variant="bodyMd" as="p" marginBlockStart="2">
              BaoBrain provides privacy-first behavioral analytics services for e-commerce merchants, including:
            </Text>
            <PolarisList type="bullet">
              <PolarisList.Item>Anonymous visitor behavior tracking (page views, clicks, scrolls)</PolarisList.Item>
              <PolarisList.Item>Conversion funnel analysis and drop-off identification</PolarisList.Item>
              <PolarisList.Item>Heatmaps and click tracking visualizations</PolarisList.Item>
              <PolarisList.Item>Session replay (anonymous only, no PII)</PolarisList.Item>
              <PolarisList.Item>A/B testing and experimentation tools</PolarisList.Item>
              <PolarisList.Item>AI-generated insights and recommendations</PolarisList.Item>
              <PolarisList.Item>Integration with Google Analytics and other platforms</PolarisList.Item>
              <PolarisList.Item>Analytics dashboards and reporting</PolarisList.Item>
            </PolarisList>
            <Text variant="bodyMd" as="p" marginBlockStart="2">
              The Services are designed to help merchants understand customer behavior, improve conversion rates, and make data-driven decisions while respecting end-user privacy.
            </Text>

            {/* 4. ELIGIBILITY */}
            <Text variant="headingMd" as="h2" marginBlockStart="8" id="eligibility">
              4. Eligibility & Account Registration
            </Text>
            <Text variant="bodyMd" as="p" marginBlockStart="2">
              <Text as="span" fontWeight="semibold">4.1 Age Requirement.</Text> You must be at least 18 years old and have the legal capacity to enter into binding contracts to use the Services.
            </Text>
            <Text variant="bodyMd" as="p" marginBlockStart="2">
              <Text as="span" fontWeight="semibold">4.2 Business Use.</Text> The Services are intended for legitimate commercial use by e-commerce merchants. You represent that you operate a bona fide e-commerce business or website.
            </Text>
            <Text variant="bodyMd" as="p" marginBlockStart="2">
              <Text as="span" fontWeight="semibold">4.3 Account Registration.</Text> To use the Services, you must create an Account. You agree to:
            </Text>
            <PolarisList type="bullet" marginBlockStart="2">
              <PolarisList.Item>Provide accurate, current, and complete information</PolarisList.Item>
              <PolarisList.Item>Maintain and promptly update your Account information</PolarisList.Item>
              <PolarisList.Item>Keep your login credentials secure and confidential</PolarisList.Item>
              <PolarisList.Item>Immediately notify us of any unauthorized access to your Account</PolarisList.Item>
              <PolarisList.Item>Accept full responsibility for all activities under your Account</PolarisList.Item>
            </PolarisList>
            <Text variant="bodyMd" as="p" marginBlockStart="2">
              <Text as="span" fontWeight="semibold">4.4 Account Limitations.</Text> You may only create one Account per business entity. Multiple accounts for the same Store are prohibited without our prior written consent.
            </Text>

            {/* 5. DATA PROCESSING & OWNERSHIP */}
            <Text variant="headingMd" as="h2" marginBlockStart="8" id="data-processing">
              5. Data Processing & Ownership
            </Text>
            <Text variant="bodyMd" as="p" marginBlockStart="2">
              <Text as="span" fontWeight="semibold">5.1 Data Controller vs Processor.</Text>
            </Text>
            <PolarisList type="bullet" marginBlockStart="2">
              <PolarisList.Item>
                <Text as="span" fontWeight="semibold">You (Merchant)</Text> are the Data Controller. You own and control all data about your Store and Customers.
              </PolarisList.Item>
              <PolarisList.Item>
                <Text as="span" fontWeight="semibold">BaoBrain</Text> is the Data Processor. We process anonymous Analytics Data on your behalf solely to provide the Services.
              </PolarisList.Item>
            </PolarisList>
            <Text variant="bodyMd" as="p" marginBlockStart="2">
              <Text as="span" fontWeight="semibold">5.2 Data Ownership.</Text> You retain all ownership rights to your Store data and content. BaoBrain claims no ownership over your data except as necessary to provide the Services.
            </Text>
            <Text variant="bodyMd" as="p" marginBlockStart="2">
              <Text as="span" fontWeight="semibold">5.3 License to Process Data.</Text> You grant BaoBrain a limited, non-exclusive, worldwide license to collect, process, store, and analyze Analytics Data from your Store for the sole purpose of providing the Services to you.
            </Text>
            <Text variant="bodyMd" as="p" marginBlockStart="2">
              <Text as="span" fontWeight="semibold">5.4 No Personal Data Processing.</Text> BaoBrain does NOT process Customer Personal Data. You agree not to configure BaoBrain or use the Services in any way that would cause Customer PII (names, emails, phone numbers, addresses, payment information) to be transmitted to or stored by BaoBrain. If a merchant attempts to send Customer PII through BaoBrain's event API, BaoBrain will discard or anonymize such data immediately.
            </Text>
            <Text variant="bodyMd" as="p" marginBlockStart="2">
              <Text as="span" fontWeight="semibold">5.5 Anonymized Data.</Text> BaoBrain may use anonymized, aggregated Analytics Data for:
            </Text>
            <PolarisList type="bullet" marginBlockStart="2">
              <PolarisList.Item>Improving the Services</PolarisList.Item>
              <PolarisList.Item>Developing new features</PolarisList.Item>
              <PolarisList.Item>Industry benchmarking (provided no individual merchant is identifiable)</PolarisList.Item>
            </PolarisList>

            {/* 6. MERCHANT RESPONSIBILITIES */}
            <Text variant="headingMd" as="h2" marginBlockStart="8" id="merchant-responsibilities">
              6. Merchant Responsibilities
            </Text>
            <Text variant="bodyMd" as="p" marginBlockStart="2">
              As a Merchant using BaoBrain, you are responsible for:
            </Text>
            <PolarisList type="bullet" marginBlockStart="2">
              <PolarisList.Item>
                <Text as="span" fontWeight="semibold">Privacy Compliance:</Text> Maintaining your own privacy policy that discloses use of analytics tools and obtaining all necessary consents from Customers as required by GDPR, CCPA, and other applicable laws
              </PolarisList.Item>
              <PolarisList.Item>
                <Text as="span" fontWeight="semibold">Cookie Notices:</Text> Providing clear notice about cookies and tracking technologies used on your Store
              </PolarisList.Item>
              <PolarisList.Item>
                <Text as="span" fontWeight="semibold">Legal Compliance:</Text> Ensuring your use of BaoBrain complies with all applicable laws, regulations, and platform policies (Shopify, WooCommerce terms, etc.)
              </PolarisList.Item>
              <PolarisList.Item>
                <Text as="span" fontWeight="semibold">Accurate Implementation:</Text> Correctly installing and configuring BaoBrain's tracking script on your Store
              </PolarisList.Item>
              <PolarisList.Item>
                <Text as="span" fontWeight="semibold">Customer Requests:</Text> Handling Customer data access, deletion, or opt-out requests in accordance with privacy laws
              </PolarisList.Item>
              <PolarisList.Item>
                <Text as="span" fontWeight="semibold">Security:</Text> Securing your Account credentials and Store platform access
              </PolarisList.Item>
            </PolarisList>
            <Text variant="bodyMd" as="p" marginBlockStart="2">
              <Text as="span" fontWeight="semibold">You acknowledge that BaoBrain provides analytics tools only. You remain solely responsible for compliance with all applicable privacy and data protection laws.</Text>
            </Text>

            {/* 7. PROHIBITED USES */}
            <Text variant="headingMd" as="h2" marginBlockStart="8" id="prohibited-use">
              7. Prohibited Uses
            </Text>
            <Text variant="bodyMd" as="p" marginBlockStart="2">
              You agree NOT to:
            </Text>
            <PolarisList type="bullet" marginBlockStart="2">
              <PolarisList.Item>Use the Services for any unlawful, fraudulent, or malicious purpose</PolarisList.Item>
              <PolarisList.Item>Attempt to identify individual Customers from anonymous Analytics Data</PolarisList.Item>
              <PolarisList.Item>Transmit or cause Customer Personal Data to be collected by BaoBrain</PolarisList.Item>
              <PolarisList.Item>Reverse-engineer, decompile, disassemble, or attempt to derive source code from the Services</PolarisList.Item>
              <PolarisList.Item>Modify, adapt, translate, or create derivative works of the Services</PolarisList.Item>
              <PolarisList.Item>Remove, obscure, or alter any proprietary notices or labels</PolarisList.Item>
              <PolarisList.Item>Use the Services to compete with BaoBrain or develop competing products</PolarisList.Item>
              <PolarisList.Item>Interfere with or disrupt the Services or servers/networks connected to the Services</PolarisList.Item>
              <PolarisList.Item>Gain unauthorized access to BaoBrain systems, other Accounts, or data</PolarisList.Item>
              <PolarisList.Item>Upload viruses, malware, or malicious code</PolarisList.Item>
              <PolarisList.Item>Use bots, scrapers, or automated tools to access the Services</PolarisList.Item>
              <PolarisList.Item>Resell, sublicense, or redistribute the Services without authorization</PolarisList.Item>
              <PolarisList.Item>Use the Services in a manner that exceeds reasonable usage limits</PolarisList.Item>
              <PolarisList.Item>Violate any applicable laws, regulations, or third-party rights</PolarisList.Item>
            </PolarisList>

            {/* 8. LICENSE */}
            <Text variant="headingMd" as="h2" marginBlockStart="8" id="license">
              8. License Grant & Restrictions
            </Text>
            <Text variant="bodyMd" as="p" marginBlockStart="2">
              <Text as="span" fontWeight="semibold">8.1 License Grant.</Text> Subject to your compliance with these Terms and payment of applicable fees, BaoBrain grants you a limited, non-exclusive, non-transferable, revocable license to access and use the Services solely for your internal business purposes.
            </Text>
            <Text variant="bodyMd" as="p" marginBlockStart="2">
              <Text as="span" fontWeight="semibold">8.2 Restrictions.</Text> This license does not grant you any right to:
            </Text>
            <PolarisList type="bullet" marginBlockStart="2">
              <PolarisList.Item>Copy, modify, or create derivative works of the Services</PolarisList.Item>
              <PolarisList.Item>Sell, rent, lease, sublicense, distribute, or otherwise transfer the Services</PolarisList.Item>
              <PolarisList.Item>Access the Services to build a competitive product or service</PolarisList.Item>
              <PolarisList.Item>Reverse engineer or attempt to extract the source code</PolarisList.Item>
              <PolarisList.Item>Use the Services beyond the scope of your subscription plan</PolarisList.Item>
            </PolarisList>
            <Text variant="bodyMd" as="p" marginBlockStart="2">
              <Text as="span" fontWeight="semibold">8.3 Reservation of Rights.</Text> BaoBrain retains all rights, title, and interest in and to the Services, including all related intellectual property rights. These Terms do not grant you any rights except as expressly stated.
            </Text>

            {/* 9. PAYMENT */}
            <Text variant="headingMd" as="h2" marginBlockStart="8" id="payment">
              9. Payment & Billing
            </Text>
            <Text variant="bodyMd" as="p" marginBlockStart="2">
              <Text as="span" fontWeight="semibold">9.1 Subscription Fees.</Text> Certain features and functionality require paid subscriptions. Pricing is available at https://baobrain.com/pricing and may be updated from time to time.
            </Text>
            <Text variant="bodyMd" as="p" marginBlockStart="2">
              <Text as="span" fontWeight="semibold">9.2 Payment Processing.</Text> Payments are processed by Stripe. By subscribing, you authorize BaoBrain to charge your payment method for all subscription fees.
            </Text>
            <Text variant="bodyMd" as="p" marginBlockStart="2">
              <Text as="span" fontWeight="semibold">9.3 Automatic Renewal.</Text> Subscriptions automatically renew unless cancelled before the renewal date. Cancellation takes effect at the end of the current billing period.
            </Text>
            <Text variant="bodyMd" as="p" marginBlockStart="2">
              <Text as="span" fontWeight="semibold">9.4 No Refunds.</Text> All fees are non-refundable except as required by law or at BaoBrain's sole discretion. If you cancel, you retain access until the end of the paid period.
            </Text>
            <Text variant="bodyMd" as="p" marginBlockStart="2">
              <Text as="span" fontWeight="semibold">9.5 Free Trials.</Text> Free trial periods may be offered at our discretion. No payment method is required for free trials unless stated otherwise. We may convert your trial to a paid subscription automatically if you provide payment information.
            </Text>
            <Text variant="bodyMd" as="p" marginBlockStart="2">
              <Text as="span" fontWeight="semibold">9.6 Price Changes.</Text> We may change subscription prices at any time. Price changes for existing subscribers take effect at the next renewal after 30 days' notice.
            </Text>
            <Text variant="bodyMd" as="p" marginBlockStart="2">
              <Text as="span" fontWeight="semibold">9.7 Taxes.</Text> You are responsible for any applicable taxes, duties, or government-imposed fees related to your subscription.
            </Text>
            <Text variant="bodyMd" as="p" marginBlockStart="2">
              <Text as="span" fontWeight="semibold">9.8 Payment Failures.</Text> If a payment fails, we may suspend or terminate your access to paid features until payment is resolved. You remain liable for unpaid amounts.
            </Text>

            {/* 10. PRIVACY */}
            <Text variant="headingMd" as="h2" marginBlockStart="8" id="privacy">
              10. Privacy & Data Protection
            </Text>
            <Text variant="bodyMd" as="p" marginBlockStart="2">
              Your use of the Services is also governed by our Privacy Policy, available at <Link url="/privacy">https://baobrain.com/privacy</Link>. By using the Services, you consent to our data practices as described in the Privacy Policy.
            </Text>
            <Text variant="bodyMd" as="p" marginBlockStart="2">
              Key points:
            </Text>
            <PolarisList type="bullet" marginBlockStart="2">
              <PolarisList.Item>BaoBrain only collects anonymous behavioral analytics data</PolarisList.Item>
              <PolarisList.Item>We do NOT collect or store Customer PII (names, emails, phone numbers, addresses)</PolarisList.Item>
              <PolarisList.Item>You (the Merchant) are the Data Controller; BaoBrain is the Data Processor</PolarisList.Item>
              <PolarisList.Item>You must comply with GDPR, CCPA, and other applicable privacy laws</PolarisList.Item>
              <PolarisList.Item>You must maintain your own privacy policy disclosing use of BaoBrain</PolarisList.Item>
            </PolarisList>

            {/* 11. INTELLECTUAL PROPERTY */}
            <Text variant="headingMd" as="h2" marginBlockStart="8" id="ip">
              11. Intellectual Property
            </Text>
            <Text variant="bodyMd" as="p" marginBlockStart="2">
              <Text as="span" fontWeight="semibold">11.1 BaoBrain's IP.</Text> The Services, including all software, algorithms, user interface designs, trademarks, logos, and content, are owned by BaoBrain or its licensors and are protected by U.S. and international copyright, trademark, patent, and other intellectual property laws.
            </Text>
            <Text variant="bodyMd" as="p" marginBlockStart="2">
              <Text as="span" fontWeight="semibold">11.2 Trademarks.</Text> "BaoBrain" and related logos are trademarks of BaoBrain LLC. You may not use our trademarks without prior written permission.
            </Text>
            <Text variant="bodyMd" as="p" marginBlockStart="2">
              <Text as="span" fontWeight="semibold">11.3 Your Content.</Text> You retain ownership of content you submit to BaoBrain (e.g., Store data, configurations). You grant us a license to use this content solely to provide the Services.
            </Text>
            <Text variant="bodyMd" as="p" marginBlockStart="2">
              <Text as="span" fontWeight="semibold">11.4 Feedback.</Text> If you provide feedback, suggestions, or ideas about the Services, you grant BaoBrain a perpetual, irrevocable, royalty-free license to use and incorporate such feedback without compensation or attribution.
            </Text>
            <Text variant="bodyMd" as="p" marginBlockStart="2">
              <Text as="span" fontWeight="semibold">11.5 DMCA Compliance.</Text> We respect intellectual property rights. If you believe content on BaoBrain infringes your copyright, contact <Link url="mailto:legal@baobrain.com">legal@baobrain.com</Link> with:
            </Text>
            <PolarisList type="bullet" marginBlockStart="2">
              <PolarisList.Item>Description of the copyrighted work</PolarisList.Item>
              <PolarisList.Item>Location of the infringing material</PolarisList.Item>
              <PolarisList.Item>Your contact information</PolarisList.Item>
              <PolarisList.Item>A statement of good faith belief that use is unauthorized</PolarisList.Item>
              <PolarisList.Item>A statement under penalty of perjury that the information is accurate</PolarisList.Item>
              <PolarisList.Item>Your physical or electronic signature</PolarisList.Item>
            </PolarisList>

            {/* 12. DISCLAIMERS */}
            <Text variant="headingMd" as="h2" marginBlockStart="8" id="disclaimers">
              12. Disclaimers & Warranties
            </Text>
            <Text variant="bodyMd" as="p" marginBlockStart="2">
              <Text as="span" fontWeight="semibold">12.1 "AS IS" Basis.</Text> THE SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED.
            </Text>
            <Text variant="bodyMd" as="p" marginBlockStart="2">
              <Text as="span" fontWeight="semibold">12.2 Disclaimer of Warranties.</Text> TO THE FULLEST EXTENT PERMITTED BY LAW, BAOBRAIN DISCLAIMS ALL WARRANTIES, INCLUDING BUT NOT LIMITED TO:
            </Text>
            <PolarisList type="bullet" marginBlockStart="2">
              <PolarisList.Item>IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT</PolarisList.Item>
              <PolarisList.Item>THAT THE SERVICES WILL BE UNINTERRUPTED, TIMELY, SECURE, OR ERROR-FREE</PolarisList.Item>
              <PolarisList.Item>THAT RESULTS FROM USE WILL BE ACCURATE OR RELIABLE</PolarisList.Item>
              <PolarisList.Item>THAT DEFECTS WILL BE CORRECTED</PolarisList.Item>
            </PolarisList>
            <Text variant="bodyMd" as="p" marginBlockStart="2">
              <Text as="span" fontWeight="semibold">12.3 No Professional Advice.</Text> BaoBrain does not provide legal, financial, or business advice. Insights and recommendations generated by the Services are for informational purposes only and should not be relied upon as professional advice.
            </Text>
            <Text variant="bodyMd" as="p" marginBlockStart="2">
              <Text as="span" fontWeight="semibold">12.4 Third-Party Services.</Text> BaoBrain is not responsible for third-party services (Shopify, Google Analytics, payment processors, etc.) or any issues arising from their use.
            </Text>
            <Text variant="bodyMd" as="p" marginBlockStart="2">
              <Text as="span" fontWeight="semibold">12.5 Data Accuracy.</Text> While we strive for accuracy, we do not guarantee that Analytics Data or AI-generated insights are complete, accurate, or free from errors.
            </Text>

            {/* 13. LIMITATION OF LIABILITY */}
            <Text variant="headingMd" as="h2" marginBlockStart="8" id="liability">
              13. Limitation of Liability
            </Text>
            <Text variant="bodyMd" as="p" marginBlockStart="2">
              <Text as="span" fontWeight="semibold">13.1 NO LIABILITY FOR DAMAGES.</Text> TO THE FULLEST EXTENT PERMITTED BY LAW, BAOBRAIN, ITS OFFICERS, DIRECTORS, EMPLOYEES, AGENTS, AND AFFILIATES SHALL NOT BE LIABLE FOR:
            </Text>
            <PolarisList type="bullet" marginBlockStart="2">
              <PolarisList.Item>INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES</PolarisList.Item>
              <PolarisList.Item>LOSS OF PROFITS, REVENUE, DATA, GOODWILL, OR BUSINESS OPPORTUNITIES</PolarisList.Item>
              <PolarisList.Item>SERVICE INTERRUPTIONS OR SECURITY BREACHES</PolarisList.Item>
              <PolarisList.Item>ERRORS, BUGS, OR INACCURACIES IN THE SERVICES</PolarisList.Item>
              <PolarisList.Item>UNAUTHORIZED ACCESS TO OR ALTERATION OF DATA</PolarisList.Item>
              <PolarisList.Item>THIRD-PARTY CONDUCT OR CONTENT</PolarisList.Item>
              <PolarisList.Item>ANY OTHER MATTER RELATING TO THE SERVICES</PolarisList.Item>
            </PolarisList>
            <Text variant="bodyMd" as="p" marginBlockStart="2">
              <Text as="span" fontWeight="semibold">13.2 Liability Cap.</Text> BaoBrain's total cumulative liability arising from or relating to these Terms or the Services shall not exceed the greater of (a) $100 or (b) the amount you paid to BaoBrain in the 12 months preceding the claim.
            </Text>
            <Text variant="bodyMd" as="p" marginBlockStart="2">
              <Text as="span" fontWeight="semibold">13.3 Exceptions.</Text> Some jurisdictions do not allow limitations on implied warranties or exclusion of certain damages. In such jurisdictions, our liability is limited to the maximum extent permitted by law.
            </Text>
            <Text variant="bodyMd" as="p" marginBlockStart="2">
              <Text as="span" fontWeight="semibold">13.4 Essential Basis.</Text> You acknowledge that these limitations of liability are a fundamental element of the agreement between you and BaoBrain. BaoBrain would not provide the Services without these limitations.
            </Text>

            {/* 14. INDEMNIFICATION */}
            <Text variant="headingMd" as="h2" marginBlockStart="8" id="indemnity">
              14. Indemnification
            </Text>
            <Text variant="bodyMd" as="p" marginBlockStart="2">
              You agree to indemnify, defend, and hold harmless BaoBrain, its officers, directors, employees, agents, affiliates, and licensors from and against any and all claims, liabilities, damages, losses, costs, expenses, or fees (including reasonable attorneys' fees) arising from or relating to:
            </Text>
            <PolarisList type="bullet" marginBlockStart="2">
              <PolarisList.Item>Your use of the Services or violation of these Terms</PolarisList.Item>
              <PolarisList.Item>Your violation of any law, regulation, or third-party rights</PolarisList.Item>
              <PolarisList.Item>Your Store content, privacy policy, or business practices</PolarisList.Item>
              <PolarisList.Item>Disputes between you and your Customers</PolarisList.Item>
              <PolarisList.Item>Any claim that your use of BaoBrain caused harm to a third party</PolarisList.Item>
              <PolarisList.Item>Your breach of privacy or data protection obligations</PolarisList.Item>
            </PolarisList>
            <Text variant="bodyMd" as="p" marginBlockStart="2">
              BaoBrain reserves the right to assume the exclusive defense and control of any matter subject to indemnification, in which case you will cooperate with BaoBrain in asserting any available defenses.
            </Text>

            {/* 15. TERMINATION */}
            <Text variant="headingMd" as="h2" marginBlockStart="8" id="termination">
              15. Termination
            </Text>
            <Text variant="bodyMd" as="p" marginBlockStart="2">
              <Text as="span" fontWeight="semibold">15.1 Termination by You.</Text> You may terminate your Account at any time by:
            </Text>
            <PolarisList type="bullet" marginBlockStart="2">
              <PolarisList.Item>Cancelling your subscription through the dashboard</PolarisList.Item>
              <PolarisList.Item>Emailing <Link url="mailto:support@baobrain.com">support@baobrain.com</Link> with your termination request</PolarisList.Item>
            </PolarisList>
            <Text variant="bodyMd" as="p" marginBlockStart="2">
              Termination takes effect at the end of your current billing period. No refunds are provided for early termination.
            </Text>
            <Text variant="bodyMd" as="p" marginBlockStart="2">
              <Text as="span" fontWeight="semibold">15.2 Termination by BaoBrain.</Text> BaoBrain may suspend or terminate your Account immediately, with or without notice, if:
            </Text>
            <PolarisList type="bullet" marginBlockStart="2">
              <PolarisList.Item>You violate these Terms</PolarisList.Item>
              <PolarisList.Item>Your payment fails or you fail to pay fees when due</PolarisList.Item>
              <PolarisList.Item>Your use creates security risks or legal liability</PolarisList.Item>
              <PolarisList.Item>You engage in fraudulent, abusive, or illegal activity</PolarisList.Item>
              <PolarisList.Item>We discontinue the Services (with 30 days' notice when feasible)</PolarisList.Item>
            </PolarisList>
            <Text variant="bodyMd" as="p" marginBlockStart="2">
              <Text as="span" fontWeight="semibold">15.3 Effect of Termination.</Text> Upon termination:
            </Text>
            <PolarisList type="bullet" marginBlockStart="2">
              <PolarisList.Item>Your right to access and use the Services immediately ceases</PolarisList.Item>
              <PolarisList.Item>BaoBrain may (but is not obligated to) delete your Account and data after 90 days</PolarisList.Item>
              <PolarisList.Item>You remain liable for all fees and charges incurred before termination</PolarisList.Item>
              <PolarisList.Item>Sections that by their nature should survive (disclaimers, limitations of liability, indemnification, dispute resolution) continue to apply</PolarisList.Item>
            </PolarisList>
            <Text variant="bodyMd" as="p" marginBlockStart="2">
              <Text as="span" fontWeight="semibold">15.4 Data Export.</Text> You may export your Analytics Data before termination. After termination, data may no longer be accessible.
            </Text>

            {/* 16. DISPUTE RESOLUTION */}
            <Text variant="headingMd" as="h2" marginBlockStart="8" id="dispute">
              16. Dispute Resolution & Arbitration
            </Text>
            <Text variant="bodyMd" as="p" marginBlockStart="2">
              <Text as="span" fontWeight="semibold">16.1 Informal Resolution.</Text> Before initiating arbitration or litigation, you agree to contact BaoBrain at <Link url="mailto:legal@baobrain.com">legal@baobrain.com</Link> to attempt to resolve the dispute informally.
            </Text>
            <Text variant="bodyMd" as="p" marginBlockStart="2">
              <Text as="span" fontWeight="semibold">16.2 Binding Arbitration.</Text> If informal resolution fails, any dispute arising from these Terms or the Services shall be resolved through binding arbitration in accordance with the American Arbitration Association's Commercial Arbitration Rules, rather than in court.
            </Text>
            <Text variant="bodyMd" as="p" marginBlockStart="2">
              <Text as="span" fontWeight="semibold">16.3 Class Action Waiver.</Text> YOU AGREE THAT DISPUTES WILL BE RESOLVED ON AN INDIVIDUAL BASIS ONLY. YOU WAIVE ANY RIGHT TO PARTICIPATE IN CLASS ACTIONS, CLASS ARBITRATIONS, OR REPRESENTATIVE PROCEEDINGS.
            </Text>
            <Text variant="bodyMd" as="p" marginBlockStart="2">
              <Text as="span" fontWeight="semibold">16.4 Exceptions.</Text> Either party may seek injunctive or equitable relief in court to protect intellectual property rights or confidential information.
            </Text>
            <Text variant="bodyMd" as="p" marginBlockStart="2">
              <Text as="span" fontWeight="semibold">16.5 Governing Law.</Text> These Terms are governed by and construed in accordance with the laws of the State of Arizona, without regard to conflict of law principles.
            </Text>

            {/* 17. SERVICE AVAILABILITY */}
            <Text variant="headingMd" as="h2" marginBlockStart="8" id="service-availability">
              17. Service Availability & Modifications
            </Text>
            <Text variant="bodyMd" as="p" marginBlockStart="2">
              <Text as="span" fontWeight="semibold">17.1 No Uptime Guarantee.</Text> We do not guarantee uninterrupted, timely, secure, or error-free access to the Services. The Services may be unavailable due to:
            </Text>
            <PolarisList type="bullet" marginBlockStart="2">
              <PolarisList.Item>Scheduled or emergency maintenance</PolarisList.Item>
              <PolarisList.Item>Technical issues, server problems, or system failures</PolarisList.Item>
              <PolarisList.Item>Network or internet connectivity issues</PolarisList.Item>
              <PolarisList.Item>Third-party service provider outages</PolarisList.Item>
              <PolarisList.Item>Force majeure events (see Section 19)</PolarisList.Item>
            </PolarisList>
            <Text variant="bodyMd" as="p" marginBlockStart="2">
              <Text as="span" fontWeight="semibold">17.2 Service Modifications.</Text> We reserve the right to modify, suspend, or discontinue any feature or aspect of the Services at any time, with or without notice. We are not liable for any modification, suspension, or discontinuation.
            </Text>
            <Text variant="bodyMd" as="p" marginBlockStart="2">
              <Text as="span" fontWeight="semibold">17.3 Updates.</Text> We may release updates, patches, or new versions of the Services. You agree that we may automatically update the Services without prior notice.
            </Text>

            {/* 18. BETA */}
            <Text variant="headingMd" as="h2" marginBlockStart="8" id="beta">
              18. Beta Features
            </Text>
            <Text variant="bodyMd" as="p" marginBlockStart="2">
              We may offer beta, pilot, or experimental features ("Beta Features") for testing purposes. Beta Features:
            </Text>
            <PolarisList type="bullet" marginBlockStart="2">
              <PolarisList.Item>Are provided "AS IS" without warranties of any kind</PolarisList.Item>
              <PolarisList.Item>May be buggy, incomplete, or unreliable</PolarisList.Item>
              <PolarisList.Item>May be changed, discontinued, or removed at any time without notice</PolarisList.Item>
              <PolarisList.Item>Are not covered by any service level agreements or support commitments</PolarisList.Item>
            </PolarisList>
            <Text variant="bodyMd" as="p" marginBlockStart="2">
              USE BETA FEATURES AT YOUR OWN RISK. WE DISCLAIM ALL LIABILITY FOR BETA FEATURES.
            </Text>

            {/* 19. FORCE MAJEURE */}
            <Text variant="headingMd" as="h2" marginBlockStart="8" id="force-majeure">
              19. Force Majeure
            </Text>
            <Text variant="bodyMd" as="p" marginBlockStart="2">
              BaoBrain is not liable for any failure or delay in performance due to circumstances beyond our reasonable control, including:
            </Text>
            <PolarisList type="bullet" marginBlockStart="2">
              <PolarisList.Item>Acts of God, natural disasters, or severe weather</PolarisList.Item>
              <PolarisList.Item>War, terrorism, civil unrest, or government action</PolarisList.Item>
              <PolarisList.Item>Pandemics, epidemics, or public health emergencies</PolarisList.Item>
              <PolarisList.Item>Internet or telecommunications failures</PolarisList.Item>
              <PolarisList.Item>Power outages or utility failures</PolarisList.Item>
              <PolarisList.Item>Third-party service provider failures</PolarisList.Item>
              <PolarisList.Item>Cyberattacks, hacking, or DDoS attacks</PolarisList.Item>
            </PolarisList>

            {/* 20. GENERAL */}
            <Text variant="headingMd" as="h2" marginBlockStart="8" id="general">
              20. General Provisions
            </Text>
            <Text variant="bodyMd" as="p" marginBlockStart="2">
              <Text as="span" fontWeight="semibold">20.1 Entire Agreement.</Text> These Terms, together with our Privacy Policy, constitute the entire agreement between you and BaoBrain and supersede all prior agreements.
            </Text>
            <Text variant="bodyMd" as="p" marginBlockStart="2">
              <Text as="span" fontWeight="semibold">20.2 Amendments.</Text> We may modify these Terms at any time by posting the revised Terms on the Site with an updated "Last Updated" date. Continued use after changes constitutes acceptance.
            </Text>
            <Text variant="bodyMd" as="p" marginBlockStart="2">
              <Text as="span" fontWeight="semibold">20.3 Assignment.</Text> You may not assign or transfer these Terms or any rights hereunder without our prior written consent. We may assign these Terms without restriction.
            </Text>
            <Text variant="bodyMd" as="p" marginBlockStart="2">
              <Text as="span" fontWeight="semibold">20.4 Severability.</Text> If any provision is found invalid or unenforceable, the remaining provisions shall remain in full force and effect.
            </Text>
            <Text variant="bodyMd" as="p" marginBlockStart="2">
              <Text as="span" fontWeight="semibold">20.5 Waiver.</Text> Failure to enforce any provision does not waive our right to enforce it later. No waiver shall be effective unless in writing and signed by both parties.
            </Text>
            <Text variant="bodyMd" as="p" marginBlockStart="2">
              <Text as="span" fontWeight="semibold">20.6 No Agency.</Text> No agency, partnership, joint venture, or employment relationship is created by these Terms.
            </Text>
            <Text variant="bodyMd" as="p" marginBlockStart="2">
              <Text as="span" fontWeight="semibold">20.7 Notices.</Text> Notices to BaoBrain should be sent to legal@baobrain.com. Notices to you will be sent to the email address associated with your Account.
            </Text>
            <Text variant="bodyMd" as="p" marginBlockStart="2">
              <Text as="span" fontWeight="semibold">20.8 Survival.</Text> Provisions that by their nature should survive termination (including but not limited to disclaimers, limitations of liability, indemnification, intellectual property rights, and dispute resolution) shall survive.
            </Text>
            <Text variant="bodyMd" as="p" marginBlockStart="2">
              <Text as="span" fontWeight="semibold">20.9 Export Control.</Text> You agree to comply with all applicable export and import control laws and regulations.
            </Text>

            {/* 21. CONTACT */}
            <Text variant="headingMd" as="h2" marginBlockStart="8" id="contact">
              21. Contact Information
            </Text>
            <Text variant="bodyMd" as="p" marginBlockStart="2">
              For questions about these Terms, contact us at:
            </Text>
            <Text variant="bodyMd" as="p" marginBlockStart="4">
              BaoBrain LLC
              <br />
              4539 N 22ND ST
              <br />
              STE R
              <br />
              PHOENIX, AZ, 85016, USA
            </Text>
            <PolarisList type="bullet" marginBlockStart="4">
              <PolarisList.Item>Legal: <Link url="mailto:legal@baobrain.com">legal@baobrain.com</Link></PolarisList.Item>
              <PolarisList.Item>Support: <Link url="mailto:support@baobrain.com">support@baobrain.com</Link></PolarisList.Item>
              <PolarisList.Item>Privacy: <Link url="mailto:privacy@baobrain.com">privacy@baobrain.com</Link></PolarisList.Item>
            </PolarisList>
          </TextContainer>
        </Layout.Section>
      </Layout>
    </Page>
  );
}